import { Close, FormatListBulletedOutlined } from '@mui/icons-material';
import { LegendButtonContainer, MenuHeader, MobileLegend, StyledLegendButton } from './styles';
import { MapLegend } from '../mapLegend';
import { useEffect, useRef, useState } from 'react';
import { Text } from '@/components/atoms/text';
import { useTranslation } from '@/hooks/useTranslation';
import { IconButton } from '@/components/atoms/Button';
import { Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Desktop } from '../viewport/desktop';
import { Mobile } from '../viewport/mobile';

interface LegendButtonProps {
  variant: 'risk' | 'opportunity';
  noBorderBottom?: boolean;
  setOpenLegend?: (open: boolean) => void;
  isOpen?: boolean;
  /** Legend panel is positioned inside the map (not viewport-fixed); list button toggles open/closed on desktop. */
  embedMode?: boolean;
}

export const LegendButton = ({
  variant,
  noBorderBottom,
  setOpenLegend: setOpenLegendProp,
  isOpen: isOpenProp,
  embedMode = false,
}: LegendButtonProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [openLegendInternal, setOpenLegendInternal] = useState(false);
  const openLegend = isOpenProp !== undefined ? isOpenProp : openLegendInternal;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpenProp !== undefined) {
      return;
    }
    if (embedMode) {
      setOpenLegendInternal(false);
      return;
    }
    if (!isMobile) {
      setOpenLegendInternal(true);
    } else {
      setOpenLegendInternal(false);
    }
  }, [isMobile, isOpenProp, embedMode]);

  // Click-outside to close when embedded in map or on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openLegend &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleCloseLegend();
      }
    };

    if (openLegend && (isMobile || embedMode)) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openLegend, isMobile, embedMode]);

  const handleOpenLegend = () => {
    setOpenLegendInternal(true);
    setOpenLegendProp?.(true);
  };

  const handleCloseLegend = () => {
    setOpenLegendInternal(false);
    setOpenLegendProp?.(false);
  };

  const toggleLegend = () => {
    if (openLegend) {
      handleCloseLegend();
    } else {
      handleOpenLegend();
    }
  };

  const useToggleForListButton = isMobile || embedMode;

  return (
    <LegendButtonContainer $embedMode={embedMode}>
      <StyledLegendButton
        variant="outlined"
        onClick={useToggleForListButton ? toggleLegend : handleOpenLegend}
        noBorderBottom={noBorderBottom ?? false}
        ref={buttonRef}
        aria-expanded={useToggleForListButton ? openLegend : undefined}
        aria-label={t('button.button.map_legend')}
      >
        <FormatListBulletedOutlined color="action" />
      </StyledLegendButton>

      <Desktop>
        {openLegend && (
          <Paper
            ref={embedMode ? menuRef : undefined}
            elevation={3}
            sx={{
              ...(embedMode
                ? {
                    position: 'absolute',
                    right: 0,
                    top: theme.spacing(6),
                    width: '256px',
                    padding: theme.spacing(2),
                    zIndex: 10,
                  }
                : {
                    position: 'fixed',
                    right: theme.spacing(8),
                    top: theme.spacing(12),
                    width: '256px',
                    padding: theme.spacing(2),
                    zIndex: 1000,
                  }),
            }}
          >
            <MenuHeader>
              <Text variant="subtitle1" color="text.primary">
                {variant === 'opportunity'
                  ? t('label.label.opportunity_level')
                  : t('label.label.risk_level')}
              </Text>

              <IconButton onClick={handleCloseLegend}>
                <Close color="action" />
              </IconButton>
            </MenuHeader>

            <MapLegend variant={variant} />
          </Paper>
        )}
      </Desktop>

      <Mobile>
        {openLegend && (
          <MobileLegend ref={menuRef}>
            <MenuHeader>
              <Text variant="subtitle1" color="text.primary">
                {variant === 'opportunity'
                  ? t('label.label.opportunity_level')
                  : t('label.label.risk_level')}
              </Text>

              <IconButton onClick={handleCloseLegend}>
                <Close color="action" />
              </IconButton>
            </MenuHeader>

            <MapLegend variant={variant} />
          </MobileLegend>
        )}
      </Mobile>
    </LegendButtonContainer>
  );
};
